"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/shared/PlaidUploadedDocumentChip";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import type { UploadedAttachmentFile } from "@/src/utils/attachmentUpload/uploadAttachmentFiles";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidUploadItem extends UploadedAttachmentFile {
  id: string;
  status?: "uploading" | "done" | "error";
}

export interface PlaidUploadedDocumentChipProps {
  file: PlaidUploadItem;
  onRemove?: () => void;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1);
  return `${size} ${units[i]}`;
}

function getFileTypeInfo(filename: string, mediaType?: string) {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf" || mediaType === "application/pdf") {
    return {
      label: "PDF",
      iconSrc: "/assets/images/plaid/manual-upload/guide-pdf.svg",
      bgClass: "bg-background_accent2_overlay10 text-content_accent2_primary",
    };
  }
  if (
    ["png", "jpg", "jpeg", "webp"].includes(ext) ||
    mediaType?.startsWith("image/")
  ) {
    return {
      label: ext.toUpperCase() || "JPG",
      iconSrc: "/assets/images/plaid/manual-upload/guide-landscape.svg",
      bgClass: "bg-background_accent5_overlay20 text-content_accent5_primary",
    };
  }
  if (
    ["csv", "xlsx", "xls"].includes(ext) ||
    mediaType?.includes("csv") ||
    mediaType?.includes("excel") ||
    mediaType?.includes("spreadsheet")
  ) {
    return {
      label: ext.toUpperCase() || "CSV",
      iconSrc: "/assets/images/plaid/manual-upload/guide-csv.svg",
      bgClass: "bg-background_auralv2_overlay10 text-content_auralv2_primary",
    };
  }
  return {
    label: ext.toUpperCase() || "DOC",
    iconSrc: "/assets/images/plaid/manual-upload/guide-csv.svg",
    bgClass: "bg-background_brand_overlay10 text-content_brand_primary",
  };
}

export const PlaidUploadedDocumentChip: React.FC<
  PlaidUploadedDocumentChipProps
> = ({ file, onRemove, className = "" }) => {
  const typeInfo = getFileTypeInfo(file.filename, file.mediaType);
  const isUploading = file.status === "uploading";
  const isError = file.status === "error";

  return (
    <div
      className={`bg-background_solid_primary border border-border_main rounded-2 p-2 flex items-center justify-between gap-2.5 transition-all shadow-sm ${className}`}
      data-testid="plaid-uploaded-doc-chip"
    >
      {/* Left thumbnail & copy */}
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <div
          className={`size-8 rounded-[6px] flex items-center justify-center shrink-0 ${typeInfo.bgClass}`}
        >
          <img
            src={typeInfo.iconSrc}
            alt=""
            aria-hidden="true"
            className="h-[18px] w-[18px]"
          />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <p className="text-xs font-medium text-content_primary truncate leading-4">
            {file.filename}
          </p>
          <p className="text-[10px] text-content_tertiary leading-3 mt-0.5">
            {/* `~...~` 为 i18n 扫描标记（review #7）：scan 会改写为 I18n.t 并生成 translate 模块 */}
            {isUploading
              ? I18n.t("Uploading...")
              : isError
                ? I18n.t("Upload failed")
                : `${typeInfo.label} · ${formatFileSize(file.size)}`}
          </p>
        </div>
      </div>

      {/* Right status or remove button */}
      <div className="shrink-0 flex items-center justify-center">
        {isUploading ? (
          <Icon
            name="Spinner"
            size={16}
            className="animate-spin text-content_brand_primary"
          />
        ) : onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            data-openreplay-label="plaidconnectflow_upload_remove"
            className="size-6 flex items-center justify-center text-content_tertiary hover:text-content_primary active:scale-95 transition-all cursor-pointer rounded-full hover:bg-black/5 dark:hover:bg-white/5"
            aria-label={I18n.t("Remove %{var0}", {
              var0: file.filename,
            })}
          >
            <Icon name="close" size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
};
